import { FC } from "react";
import { Box, Group, Paper, Stack } from "@mantine/core";

import { Issue, IssueType } from "@protos/issues/v1/issues";
import { InlineMarkdownCode } from "@shared/components";
import { Rule } from "@shared/types";
import { formatTextAsMarkdown } from "@shared/utils";

import { RemediationInfo } from "./RemediationInfo/RemediationInfo";
import { ReachabilityInfo } from "./ReachabilityInfo";
import { RuleIconInfoList } from "./RuleIconInfoList";
import { RuleReferences } from "./RuleReferences";
import { RuleTitle, RuleTitleProps } from "./RuleTitle";
import { ScaRuleTitle } from "./ScaRuleTitle";
import { SecretsRuleTitle } from "./SecretsRuleTitle";

const RULE_TITLE_COMPONENTS_MAP: Record<IssueType, FC<RuleTitleProps> | null> =
  {
    [IssueType.SCA]: ScaRuleTitle,
    [IssueType.SAST]: RuleTitle,
    [IssueType.SECRETS]: SecretsRuleTitle,
    [IssueType.RESEARCH]: null,
    [IssueType.UNSPECIFIED]: null,
  };

export interface RuleInformationCardProps {
  finding: Issue;
  rule?: Rule;
}
/**
 *
 * @param findingData - the data for the current finding
 * @param rule - the rule that the finding was generated from
 * Note: the rule information on the finding can be out-of-date, which is why we need to retrieve information from the rule itself.
 * @returns the rule information card. Specifically for use with the finding details page.
 */
export const RuleInformationCard: FC<RuleInformationCardProps> = ({
  finding,
  rule,
}) => {
  const RuleTitle = RULE_TITLE_COMPONENTS_MAP[finding.issueType];

  return (
    <Paper withBorder p="lg" h="100%">
      <Stack gap={24}>
        <Group justify="space-between">
          {RuleTitle ? <RuleTitle finding={finding} /> : null}
          <RuleIconInfoList finding={finding} rule={rule} />
        </Group>
        {finding.message && (
          <Box c="gray.7">
            <InlineMarkdownCode text={formatTextAsMarkdown(finding.message)} />
          </Box>
        )}
        {finding.issueType === IssueType.SCA && (
          <ReachabilityInfo finding={finding} />
        )}
        {finding.issueType === IssueType.SCA && (
          <RemediationInfo finding={finding} />
        )}
        <RuleReferences finding={finding} />
      </Stack>
    </Paper>
  );
};
